<script setup lang="ts" generic="T extends SelectableValue">
export type FormComboboxProps<T extends SelectableValue> = SelectBoxProps<T> & FormFieldProps;

import type { FormFieldProps, FormFieldSlots } from '@/components/formField/FormField';
import type { Selectable, SelectableValue, SelectBoxProps } from '@/components/selectBox/selectBox';

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

import { useFormField } from '@/components/formField/useFormField';

import FormField from '@/components/formField/FormField.vue';
import Combobox from '@/components/selectBox/combobox/ComboboxInput.vue';
import MobileComboboxInput from '@/components/selectBox/combobox/MobileComboboxInput.vue';

const props = defineProps<
  FormComboboxProps<T> & {
    clearable?: boolean;
  }
>();
defineSlots<FormFieldSlots>();

const emit = defineEmits<{
  'update:modelValue': [Selectable<T> | null];
  open: [];
}>();

function onValueChange(value: Selectable<T> | null): void {
  emit('update:modelValue', value);
}

const { formFieldProps, slots } = useFormField({ props });

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMdAndAbove = breakpoints.greaterOrEqual('md');
</script>

<template>
  <FormField v-bind="formFieldProps">
    <Combobox
      v-if="isMdAndAbove"
      :clearable="clearable"
      :hasError="hasError"
      :iconLeft="iconLeft"
      :isDisabled="isDisabled"
      :isLoading="isLoading"
      :isReadonly="isReadonly"
      :modelValue="modelValue as Selectable<T>"
      :onFilter="onFilter"
      :options="options"
      :placeholder="placeholder"
      :showQueryAsOption="showQueryAsOption"
      @update:modelValue="onValueChange"
      @open="$emit('open')"
    />
    <MobileComboboxInput
      v-else
      :clearable="clearable"
      :hasError="hasError"
      :iconLeft="iconLeft"
      :isDisabled="isDisabled"
      :isLoading="isLoading"
      :isReadonly="isReadonly"
      :modelValue="modelValue as Selectable<T>"
      :onFilter="onFilter"
      :options="options"
      :placeholder="placeholder"
      :showQueryAsOption="showQueryAsOption"
      @update:modelValue="onValueChange"
      @open="$emit('open')"
    />
    <template v-for="slot of slots" #[slot]>
      <slot :name="slot" />
    </template>
  </FormField>
</template>
